import { Button, TextField, Typography } from "@material-ui/core";
import { Theme, makeStyles } from "@material-ui/core/styles";
import { Events, User } from "app-interfaces";
import { addMinutes } from "date-fns";
import React from "react";
import { useTranslation } from "react-i18next";

import { useStateValue } from "../../config/AppContext";
import { Modal, SelectClientsModal } from "../Modals";
import { TimePickerDropdown as TimePicker } from "../Utilities/TimePickerDropdown";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flexGrow: 1,
  },
  session: {
    marginTop: 30,
    "& .session-time": {
      fontSize: 17,
      lineHeigth: 20,
      color: "#525252",
    },
    "& .MuiTypography-body1": {
      marginBottom: theme.spacing(3),
      fontSize: 17,
      color: "#525252",
    },
    "& .MuiFormControl-root": {
      marginBottom: theme.spacing(2),
    },
    "& .switch": {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3),
      fontSize: 17,
      fontWeight: "bold",
      color: "#000000",
      "& .toggle-container": {
        display: "inline-block",
        float: "none",
        marginTop: -3,
        marginLeft: 15,
        verticalAlign: "middle",
      },
    },
    "& #session-add-client": {
      "& button": {
        height: 45,
        fontSize: 16,
        textTransform: "none",
        borderColor: "var(--text-primary)",
        color: "var(--text-primary)",
        "&:hover": {
          backgroundColor: "var(--text-primary)",
          color: "#ffffff",
        },
      },
      "& .client-info": {
        width: "100%",
        "& .MuiTypography-subtitle1": {
          display: "inline-block",
          width: "50%",
          fontWeight: "bold",
          fontSize: 17,
          color: "#525252",
        },
        "& .MuiTypography-subtitle2": {
          display: "inline-block",
          width: "50%",
          marginTop: 0,
          fontSize: 17,
          color: "#525252",
        },
      },
    },
  },
}));

type SessionProps = {
  event: Events.IAgenda;
  time: { start: string; end: string; datetime: Date };
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
};
type SessionSectionProps = {
  event: Events.IAgenda;
  start: Events.IAgenda["ev_start_time"];
  from: { hours: number; minutes: number };
  to: { hours: number; minutes: number };
  toggle: boolean;
  customer_id?: string;
  workout_id?: string;
  setToggle: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function SessionAdd({ event, time, onSubmit }: SessionProps) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [toggle, setToggle] = React.useState(event.session_type.toString() === "BLOCKED_TIME");
  const [validationErrorMessage, setValidationErrorMessage] = React.useState<string | null>(null);
  const sessionType = toggle ? "BLOCKED_TIME" : "SESSION";
  const props: SessionSectionProps = {
    toggle,
    setToggle,
    start: event.ev_start_time,
    from: {
      hours: parseInt(time.start.split(":")[0]),
      minutes: parseInt(time.start.split(":")[1]),
    },
    to: {
      hours: parseInt(time.end.split(":")[0]),
      minutes: parseInt(time.end.split(":")[1]),
    },
    event,
  };
  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) =>
    Object.assign(sessionType, e.currentTarget.value);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const customerId = formData.get("customer_id");
    const newSessionType = formData.get("type") as "BLOCKED_TIME" | "SESSION";
    const originalSessionType = event.session_type;

    // should not display if trying to remove blocked time
    if (originalSessionType === "ADDNEW" && newSessionType === "SESSION" && !customerId) {
      setValidationErrorMessage(
        'Add a client or select the "Personal time" option to be able so save.'
      );

      return false;
    }

    onSubmit(e);
  };

  return (
    <div id="addSession" className={classes.session}>
      <form onSubmit={handleSubmit}>
        <input type="text" name="id" defaultValue={event.id} style={{ display: "none" }} />
        <TimeSection {...props} />
        <PersonalSection {...props} />
        <ClientSection {...props} />
        <input
          type="text"
          name="type"
          value={sessionType}
          onChange={handleChange}
          style={{ display: "none" }}
        />
        <button id="session-submit" type="submit" style={{ display: "none" }} />
      </form>
      {validationErrorMessage && (
        <Modal
          open={true}
          message={t(validationErrorMessage)}
          title={t("Error")}
          onClose={() => {
            setValidationErrorMessage(null);
          }}
        />
      )}
    </div>
  );
}

const TimeSection: React.FC<SessionSectionProps> = (props) => {
  const { t } = useTranslation();
  const { from, to } = props;
  return (
    <div id="session-time" className="session-time">
      {t("Time available from")} {from.hours}:
      {from.minutes < 10 ? `0${from.minutes}` : `${from.minutes}`} - {to.hours}:
      {to.minutes < 10 ? `0${to.minutes}` : `${to.minutes}`}
    </div>
  );
};

function formatDate(dateTime: Date) {
  return dateTime.toLocaleString().replace(" AM", "").replace(" PM", "").split(", ")[1];
}

const PersonalSection: React.FC<SessionSectionProps> = (props) => {
  const { t } = useTranslation();
  const { event, start, from, to, toggle, setToggle } = props;
  const { settings } = useStateValue() as { settings: User.IUserSettings };
  const newStart = formatDate(addMinutes(new Date(start), settings.session_interval));
  const newEnd = formatDate(
    addMinutes(new Date(start), settings.session_interval + settings.session_length)
  );
  const maxTime = formatDate(
    addMinutes(
      new Date(start),
      settings.session_interval + settings.session_length + settings.session_length_long
    )
  );
  const checkPM = () => {
    const testDate = addMinutes(new Date(start), settings.session_interval).toLocaleString();
    return /PM/.test(testDate);
  };
  const options =
    event.session_type.toString() === "BLOCKED_TIME"
      ? {
          from,
          to,
          disabled: false,
        }
      : {
          from: {
            hours: checkPM()
              ? parseInt(newStart?.split(":")[0]) + 12
              : parseInt(newStart?.split(":")[0]),
            minutes: parseInt(newStart?.split(":")[1]),
          },
          to: {
            hours: checkPM()
              ? parseInt(newEnd?.split(":")[0]) + 12
              : parseInt(newEnd?.split(":")[0]),
            minutes: parseInt(newEnd?.split(":")[1]),
          },
          maxTime,
          timeIntervals: 5,
          timeCaption: toggle ? t("Blocked") : t("Session"),
          disabled: false,
        };
  return (
    <>
      <label className="switch">
        {t("PERSONAL TIME")}
        <input id="toggle" type="checkbox" defaultChecked={toggle} style={{ display: "none" }} />
        <span
          className={toggle ? "toggle-container toggled" : "toggle-container"}
          onClick={() => setToggle(!toggle)}
        >
          <span className={"toggle-button"} />
        </span>
      </label>
      {toggle ? (
        <TextField
          fullWidth
          type="text"
          name="name"
          variant="outlined"
          label={t("Description")}
          defaultValue={event.session_type.toString() === "BLOCKED_TIME" ? event.name : ""}
          InputLabelProps={{
            style: {
              marginTop: 0,
            },
          }}
        />
      ) : (
        <Typography variant="body1">{t("You can reserve your time for personal needs")}</Typography>
      )}
      <div style={{ padding: 15 }}>
        <TimePicker {...options} {...props} />
      </div>
    </>
  );
};

const InviteSection: React.FC<SessionSectionProps> = (props) => {
  const { t } = useTranslation();
  const [invite, setInvite] = React.useState(false);
  return (
    <label className="switch">
      {t("Invite by email")}
      <input
        id="invite"
        name="email-invite"
        type="checkbox"
        defaultChecked={invite}
        style={{ display: "none" }}
      />
      <span
        className={invite ? "toggle-container toggled" : "toggle-container"}
        onClick={() => setInvite(!invite)}
      >
        <span className={"toggle-button"} />
      </span>
    </label>
  );
};

const ClientSection: React.FC<SessionSectionProps> = (props) => {
  const { t } = useTranslation();
  const { clients } = useStateValue() as { clients: User.IUserClient[] };
  const { toggle } = props;
  const [show, setShow] = React.useState(false);
  const [selected, setSelected] = React.useState<(User.IUserClient | undefined)[]>();

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const formData = new FormData(e.currentTarget);
    const payload = formData.get("clients");
    if (typeof payload === "string") {
      const customerIds = JSON.parse(payload) as string[];
      const selectedClients = customerIds.map((id) => clients.find((el) => el.id === id));
      setSelected(selectedClients);
    }
    return setShow(false);
  };

  return toggle ? (
    <></>
  ) : (
    <div id="session-add-client">
      <InviteSection {...props} />
      <Button
        variant="outlined"
        onClick={(e) => {
          e.preventDefault();
          return setShow(true);
        }}
      >
        {selected ? t("Edit client") : t("Add client")}
      </Button>
      {selected && (
        <div style={{ margin: "10px" }}>
          <input
            name="customer_id"
            style={{ display: "none" }}
            defaultValue={`${selected.map((el) => (el ? el.id : ""))}`}
          />

          {selected.map(
            (el, i) =>
              el && (
                <div key={el.id} className="client-info">
                  <Typography variant="subtitle1" noWrap>
                    {el.first_name} {el.last_name}{" "}
                  </Typography>
                  <Typography variant="subtitle2" noWrap>
                    {el.email}
                  </Typography>
                </div>
              )
          )}
        </div>
      )}
      <SelectClientsModal show={show} onHide={() => setShow(false)} onSubmit={handleSubmit} />
    </div>
  );
};
